import {
  createDataSourceDefinition,
  type DataSourceDefinition,
  type FiltersDefinition,
  type RecordType,
  type SortingsDefinition,
} from '@factorialco/f0-react'
import { useMemo } from 'react'

import { jobContractTypeLabel } from 'modules/ats/lib/utils/jobPosting'
import type { AtsContractTypesEnum } from 'generated/resources/graphql'

export interface ContractTypeOption extends RecordType {
  id: AtsContractTypesEnum
  name: string
}

// Mirrors `Ats::ContractTypes` (backend T::Enum). Reuses `jobContractTypeLabel`
// from ATS for label resolution; that helper covers the 5 explicitly-translated
// values and falls back to a humanized label for the rest.
const CONTRACT_TYPE_VALUES: readonly AtsContractTypesEnum[] = [
  'indefinite',
  'temporary',
  'intern',
  'training',
  'freelance',
  'vendor_contractor',
  'volunteer',
  'per_hour',
  'other',
  'alternant',
  'interim',
  'minijob',
  'werkstudent',
  'apprenticeship',
  'pj',
  'clt',
  'jovem_aprendiz',
  'a_termo_incerto',
  'a_termo_certo',
  'de_curta_duracao',
  'de_muita_curta_duracao',
  'promessa_de_trabalho',
  'a_tempo_parcial',
  'com_pluralidade_de_empregadores',
  'teletrabalho',
  'pre_reforma',
  'recibos_verdes',
  'estagio',
  'sem_termo',
  'apprentissage',
  'fixed_discontinued',
  'apprendistato',
]

export const contractTypeLabel = (value: AtsContractTypesEnum): string =>
  jobContractTypeLabel(value)

const useHeadcountContractTypeSource = (): DataSourceDefinition<
  ContractTypeOption,
  FiltersDefinition,
  SortingsDefinition
> => {
  return useMemo(
    () =>
      createDataSourceDefinition<ContractTypeOption, FiltersDefinition, SortingsDefinition>({
        dataAdapter: {
          fetchData: async ({ search }) => {
            const records = CONTRACT_TYPE_VALUES.map(
              (id): ContractTypeOption => ({ id, name: contractTypeLabel(id) })
            )
              .filter(
                (option) => !search || option.name.toLowerCase().includes(search.toLowerCase())
              )
              .sort((a, b) => a.name.localeCompare(b.name))

            return Promise.resolve({ records })
          },
        },
      }),
    []
  )
}

export default useHeadcountContractTypeSource
